import {
  Avatar, Box, Button, Card, CardActions, CardContent, Chip, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Stack, Tab, Tabs, TextField, Typography, Alert,
} from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '../../shared/api';
import type { Alumni, Post, PostType } from '../../shared/types';
import { formatDate } from '../../shared/utils';

const TYPE_LABELS: Record<PostType | 'ALL', string> = {
  ALL: 'All', ADMISSION: 'Admissions', JOB_OPENING: 'Careers', EVENT: 'Events', ALUMNI: 'Alumni',
};

export default function AlumniPage() {
  const [tab, setTab] = useState<PostType | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [applyPost, setApplyPost] = useState<Post | null>(null);

  const postsQ = useQuery({
    queryKey: ['public-posts', tab, search],
    queryFn: async () => {
      const params: any = {};
      if (tab !== 'ALL') params.type = tab;
      if (search) params.search = search;
      return (await api.get<Post[]>('/public/posts', { params })).data;
    },
  });
  const alumniQ = useQuery({
    queryKey: ['public-alumni'],
    queryFn: async () => (await api.get<Alumni[]>('/public/alumni')).data,
  });

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.dark', color: '#fff', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ opacity: 0.7 }}>Alumni & Posts</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>News, Admissions, Careers & Events</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
            {(Object.keys(TYPE_LABELS) as Array<PostType | 'ALL'>).map((k) => (
              <Tab key={k} value={k} label={TYPE_LABELS[k]} />
            ))}
          </Tabs>
          <TextField
            placeholder="Search posts…" size="small" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 320 }}
          />
        </Stack>

        <Grid container spacing={3}>
          {(postsQ.data ?? []).map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {p.imageUrl && <Box component="img" src={p.imageUrl} sx={{ width: '100%', height: 180, objectFit: 'cover' }} />}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={TYPE_LABELS[p.type]} size="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">{formatDate(p.createdAt)}</Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{p.body}</Typography>
                </CardContent>
                {p.applyEnabled && (
                  <CardActions sx={{ p: 2 }}>
                    <Button variant="contained" onClick={() => setApplyPost(p)}>
                      {p.type === 'JOB_OPENING' ? 'Apply for this role' : p.type === 'ADMISSION' ? 'Apply for admission' : 'Register'}
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
          {postsQ.data && postsQ.data.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No posts match your filters.</Typography>
            </Grid>
          )}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: '#F1F5F9', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Featured Alumni</Typography>
          <Grid container spacing={3}>
            {(alumniQ.data ?? []).map((a) => (
              <Grid item xs={12} sm={6} md={3} key={a.id}>
                <Card>
                  <CardContent>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mb: 2, fontWeight: 700 }}>
                      {a.name.split(' ').map((s) => s[0]).join('').slice(0, 2)}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{a.name}</Typography>
                    <Typography variant="caption" color="text.secondary">Batch {a.batchYear}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>{a.currentRole}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{a.achievements}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <ApplyDialog post={applyPost} onClose={() => setApplyPost(null)} />
    </Box>
  );
}

function ApplyDialog({ post, onClose }: { post: Post | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const submit = useMutation({
    mutationFn: async () => {
      if (!post) return;
      return (await api.post('/public/applications', {
        postId: post.id, applicantData: { name, email, phone, message },
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries();
      setName(''); setEmail(''); setPhone(''); setMessage('');
    },
  });

  return (
    <Dialog open={!!post} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{post?.type === 'JOB_OPENING' ? 'Apply for' : post?.type === 'ADMISSION' ? 'Admission Form' : 'Register for'}: {post?.title}</DialogTitle>
      <DialogContent>
        {submit.isSuccess ? (
          <Alert severity="success">Your application has been submitted. We will get back to you soon.</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Full name" required value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Phone" required value={phone} onChange={(e) => setPhone(e.target.value)} />
            <TextField label="Message / cover note" multiline minRows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
            {submit.isError && <Alert severity="error">{extractError(submit.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {!submit.isSuccess && (
          <Button variant="contained" onClick={() => submit.mutate()} disabled={submit.isPending || !name || !email || !phone}>
            {submit.isPending ? 'Submitting…' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
